/**
 * useAutoSave — debounced auto-save hook for editors.
 *
 * Phase 8 Prompt 3 — per audit deliverable 04D Pattern P9.
 *
 * Cadence: saves 5 seconds after the last change (debounced); also fires on
 * `forceSave()` (e.g. when wizard advances to next step) and on `flushOnUnmount`.
 *
 * Status state machine:
 *   idle → saving → saved → idle
 *                ↘ error → idle (after retry button)
 *
 * Usage in a wizard step:
 *   const { status, lastSaved, forceSave } = useAutoSave(
 *     formState,
 *     async (data) => apiService.put(`/api/admin/campaigns/${id}`, data),
 *     { debounceMs: 5000 }
 *   );
 *
 *   <Box>Status: {status === 'saved' && `Saved ${secondsAgo(lastSaved)}s ago`}</Box>
 *   <Button onClick={forceSave}>Save Draft</Button>
 */

import { useState, useRef, useEffect, useCallback } from "react";

export type AutoSaveStatus = "idle" | "saving" | "saved" | "error";

export interface UseAutoSaveOptions<T> {
  /** Debounce delay in ms before saving after the last change. Default 5000ms. */
  debounceMs?: number;
  /** Whether to flush a pending save on component unmount. Default true. */
  flushOnUnmount?: boolean;
  /** Called on successful save. */
  onSuccess?: (data: T) => void;
  /** Called on save failure. */
  onError?: (err: Error) => void;
  /** Whether the hook is enabled. Set false to disable (e.g. read-only mode). */
  enabled?: boolean;
}

export interface UseAutoSaveReturn<T> {
  /** Current save status. */
  status: AutoSaveStatus;
  /** Timestamp of last successful save. */
  lastSaved: Date | null;
  /** Last error if status === 'error'. */
  lastError: Error | null;
  /** Force an immediate save (e.g. user clicked "Save Draft"). */
  forceSave: () => Promise<void>;
  /** Cancel any pending debounced save (e.g. user clicked "Discard"). */
  cancelPending: () => void;
  /** Whether the current data is "dirty" (has unsaved changes). */
  isDirty: boolean;
}

/**
 * Returns a stable JSON-ish hash of a value for change detection. Uses
 * JSON.stringify with stable key ordering at top level (good enough for
 * shallow form-state comparison).
 */
function shallowSerialize(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export function useAutoSave<T>(
  data: T,
  saveFn: (data: T) => Promise<void>,
  options: UseAutoSaveOptions<T> = {},
): UseAutoSaveReturn<T> {
  const {
    debounceMs = 5000,
    flushOnUnmount = true,
    onSuccess,
    onError,
    enabled = true,
  } = options;

  const [status, setStatus] = useState<AutoSaveStatus>("idle");
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [lastError, setLastError] = useState<Error | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  // Track current data + the last-saved-snapshot to compute dirty state.
  const dataRef = useRef<T>(data);
  const lastSavedHashRef = useRef<string | null>(null);
  const lastQueuedHashRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef<boolean>(false);

  // Update dataRef every render so forceSave/flush see the latest value.
  useEffect(() => {
    dataRef.current = data;

    const currentHash = shallowSerialize(data);
    setIsDirty(currentHash !== lastSavedHashRef.current);

    if (!enabled) return;

    // Schedule a debounced save when data changes.
    if (currentHash !== lastQueuedHashRef.current) {
      lastQueuedHashRef.current = currentHash;

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        performSave();
      }, debounceMs);
    }

    return () => {
      // Don't clear here — let next effect or unmount handler manage it.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, debounceMs, enabled]);

  const performSave = useCallback(async () => {
    if (inFlightRef.current) return; // Avoid double-fire
    const snapshotHash = shallowSerialize(dataRef.current);
    if (snapshotHash === lastSavedHashRef.current) return; // No-op if clean

    inFlightRef.current = true;
    setStatus("saving");
    setLastError(null);

    try {
      await saveFn(dataRef.current);
      lastSavedHashRef.current = snapshotHash;
      setLastSaved(new Date());
      setStatus("saved");
      setIsDirty(false);
      if (onSuccess) onSuccess(dataRef.current);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setLastError(error);
      setStatus("error");
      if (onError) onError(error);
    } finally {
      inFlightRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveFn, onSuccess, onError]);

  const forceSave = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    await performSave();
  }, [performSave]);

  const cancelPending = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    lastQueuedHashRef.current = lastSavedHashRef.current;
  }, []);

  // Flush on unmount (if dirty)
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (flushOnUnmount && enabled) {
        const currentHash = shallowSerialize(dataRef.current);
        if (currentHash !== lastSavedHashRef.current) {
          // Fire-and-forget; component is unmounting so we can't await.
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          performSave();
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flushOnUnmount, enabled]);

  return {
    status,
    lastSaved,
    lastError,
    forceSave,
    cancelPending,
    isDirty,
  };
}

/**
 * Friendly relative-time helper for the "Saved Xs ago" indicator.
 */
export function secondsAgo(date: Date | null): string {
  if (!date) return "";
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}
